import { CSSProperties, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { MotionValue } from "framer-motion";
import { ArrowUpRight, Calendar, Star } from "lucide-react";
import { SiGithub } from "react-icons/si";
import SceneTrack, { useSceneRender } from "../SceneTrack";
import SceneCanvas from "../SceneCanvas";
import { box, easeOut, lerp, place, range, smooth } from "../morph";
import { PROJECTS, Project } from "../../../data/projects";
import { DESKTOP_NODE, PHONE_NODE, PortraitSchematic, portrait } from "./workPortrait";
import "./WorkScene.css";

/*
 * 04 · Work — "Schematic Unfold".
 *
 * The project card opens into a drafting table. Each tech tag lifts off its
 * row, flies an arc to its place and turns from a pill into a node of the
 * system's architecture; the wires draw themselves; the highlights pin onto
 * the parts of the diagram they're about. One scene per project.
 */

const BOUNDARY_D = "M548 170 H1108 Q1120 170 1120 182 V508 Q1120 520 1108 520 H462 Q450 520 450 508 V182 Q450 170 462 170";

type XYS = [number, number, number];

/**
 * Where everything goes, before (`0`) and after (`1`) the card unfolds.
 * Desktop is the 1200×720 drafting table; the phone version stacks the card
 * text, wraps the tags into rows, transposes the diagram (workPortrait.ts)
 * and lists the annotations as a numbered legend under it.
 */
interface WorkLayout {
    phone: boolean;
    canvas: { width: number; height: number; navBand: number; pad: number; className: string };
    node: { w: number; h: number };
    card: [number, number, number, number, number, number, number, number];
    meta: [XYS, XYS];
    title: [XYS, XYS];
    role: [XYS, XYS];
    desc: [number, number];
    summary: [number, number];
    /** First highlight line; each next one `step` below. */
    hl: [number, number, number];
    /** Tag row: start x, y, the right edge to wrap at, row step. */
    tags: [number, number, number, number];
    actions: [number, number, number, number];
    /** Annotation column: badge x0, y0, x1, y1, row steps before/after, short x. */
    ann: { x0: number; y0: number; x1: number; y1: number; step0: number; step1: number; shortX: number };
    /** Where a tag with no target settles. */
    stray: [number, number];
}

const DESKTOP: WorkLayout = {
    phone: false,
    canvas: { width: 1200, height: 720, navBand: 56, pad: 48, className: "" },
    node: DESKTOP_NODE,
    card: [150, 205, 900, 410, 40, 72, 1120, 624],
    meta: [[182, 233, 1], [64, 92, 0.9]],
    title: [[182, 263, 1], [64, 118, 0.78]],
    role: [[182, 299, 1], [64, 146, 0.9]],
    desc: [182, 325],
    summary: [64, 176],
    hl: [182, 383, 24],
    tags: [182, 503, Infinity, 0],
    actions: [182, 555, 924, 652],
    ann: { x0: 184, y0: 386, x1: 64, y1: 268, step0: 24, step1: 36, shortX: 92 },
    stray: [470, 640],
};

const PHONE: WorkLayout = {
    phone: true,
    canvas: { width: 390, height: 844, navBand: 64, pad: 0, className: "scene-canvas--phone" },
    node: PHONE_NODE,
    card: [16, 236, 358, 424, 8, 72, 374, 764],
    meta: [[32, 254, 1], [24, 90, 0.9]],
    title: [[32, 282, 1], [24, 114, 0.78]],
    role: [[32, 338, 1], [24, 154, 0.9]],
    desc: [32, 362],
    summary: [24, 176],
    hl: [32, 434, 22],
    tags: [32, 534, 358, 34],
    actions: [32, 620, 24, 800],
    ann: { x0: 34, y0: 437, x1: 24, y1: 656, step0: 22, step1: 34, shortX: 50 },
    stray: [24, 622],
};

function edgeBeat(i: number, edgeCount: number) {
    const a = 0.56 + (i * 0.1) / Math.max(1, edgeCount - 1);
    return [a, a + 0.04] as const;
}

export default function WorkScene({ phone = false }: { phone?: boolean }) {
    const L = phone ? PHONE : DESKTOP;
    return (
        <>
            {PROJECTS.map((project, i) => (
                <SceneTrack key={project.title} pin={phone ? 220 : 180} tint="124, 146, 255">
                    {(p) => <WorkCanvas p={p} project={project} index={i} L={L} />}
                </SceneTrack>
            ))}
        </>
    );
}

interface TagGeom {
    x0: number;
    y0: number;
    w0: number;
    to: { x: number; y: number; w: number; h: number; r: number };
    isNode: boolean;
}

function targetFor(schematic: Diagram, L: WorkLayout, tech: string, w0: number) {
    const { w: NODE_W, h: NODE_H } = L.node;
    const target = schematic.tagTargets[tech] ?? "";
    const node = (id: string) => schematic.nodes.find((n) => n.id === id);
    if (target === "boundary") return { to: { x: schematic.boundaryLabel.x, y: schematic.boundaryLabel.y, w: w0, h: 24, r: 5 }, isNode: false };
    if (target.indexOf("badge:") === 0) {
        const n = node(target.slice(6));
        if (n) return { to: { x: n.x + NODE_W / 2 - 22, y: n.y - NODE_H / 2 - 12, w: 28, h: 22, r: 5 }, isNode: false };
    }
    const n = node(target);
    if (n) return { to: { x: n.x - NODE_W / 2, y: n.y - NODE_H / 2, w: NODE_W, h: NODE_H, r: 6 }, isNode: true };
    // A tag with nowhere to go settles just under the diagram.
    return { to: { x: L.stray[0], y: L.stray[1], w: w0, h: 28, r: 14 }, isNode: false };
}

type Diagram = Omit<PortraitSchematic, "sideLabels"> & { sideLabels?: boolean[] };

function diagramFor(project: Project, L: WorkLayout): Diagram {
    if (L.phone) return portrait(project.schematic);
    return { ...project.schematic, boundaryD: BOUNDARY_D, boundaryLabel: { x: 462, y: 158 } };
}

function WorkCanvas({ p, project, index, L }: { p: MotionValue<number>; project: Project; index: number; L: WorkLayout }) {
    const root = useRef<HTMLDivElement>(null);
    const schematic = useMemo(() => diagramFor(project, L), [project, L]);
    const { w: NODE_W, h: NODE_H } = L.node;
    const edgeCount = schematic.edges.length;
    const els = useRef<{
        q: (k: string) => HTMLElement;
        hl: HTMLElement[];
        tags: HTMLElement[];
        roles: (HTMLElement | null)[];
        ext: HTMLElement[];
        edges: { path: SVGPathElement; len: number; tip: SVGCircleElement; pk: SVGCircleElement; label: HTMLElement | null }[];
        boundary: { path: SVGPathElement; len: number };
        ann: { badge: HTMLElement; short: HTMLElement; text: HTMLElement }[];
    } | null>(null);
    const geom = useRef<TagGeom[]>([]);

    useLayoutEffect(() => {
        const r = root.current;
        if (!r) return;
        const all = <T extends Element>(s: string) => Array.from(r.querySelectorAll(s)) as T[];

        const measure = () => {
            const tags = all<HTMLElement>(".wk-tag");
            const [tx, ty, wrap, rowStep] = L.tags;
            let x = tx;
            let y = ty;
            geom.current = tags.map((el, i) => {
                el.style.width = "auto";
                const w0 = el.offsetWidth;
                if (x > tx && x + w0 > wrap) {
                    x = tx;
                    y += rowStep;
                }
                const g = { x0: x, y0: y, w0, ...targetFor(schematic, L, project.tech[i].name, w0) };
                x += w0 + 8;
                return g;
            });
            const paths = all<SVGPathElement>(".wk-edge");
            const boundary = r.querySelector(".wk-boundary") as SVGPathElement;
            els.current = {
                q: (k) => r.querySelector(`[data-wk="${k}"]`) as HTMLElement,
                hl: all<HTMLElement>(".wk-hl"),
                tags,
                roles: tags.map((t) => t.querySelector(".wk-tag-role") as HTMLElement | null),
                ext: all<HTMLElement>(".wk-ext"),
                edges: paths.map((path, i) => {
                    const len = path.getTotalLength();
                    path.style.strokeDasharray = String(len);
                    return {
                        path,
                        len,
                        tip: r.querySelector(`[data-wk-tip="${i}"]`) as SVGCircleElement,
                        pk: r.querySelector(`[data-wk-pk="${i}"]`) as SVGCircleElement,
                        label: r.querySelector(`[data-wk-label="${i}"]`) as HTMLElement | null,
                    };
                }),
                boundary: { path: boundary, len: boundary.getTotalLength() },
                ann: all<HTMLElement>(".wk-ann-badge").map((badge, i) => ({
                    badge,
                    short: r.querySelector(`[data-wk-short="${i}"]`) as HTMLElement,
                    text: r.querySelector(`[data-wk-text="${i}"]`) as HTMLElement,
                })),
            };
            els.current.boundary.path.style.strokeDasharray = String(els.current.boundary.len);
        };

        measure();
        let cancelled = false;
        document.fonts?.ready.then(() => {
            if (cancelled) return;
            measure();
            render(p.get());
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project, schematic]);

    const render = (v: number) => {
        const E = els.current;
        if (!E) return;

        const hk = range(v, 0.1, 0.2);
        const head = E.q("head");
        head.style.opacity = String(1 - hk);
        head.style.transform = `translateY(${-10 * hk}px)`;

        const k = easeOut(range(v, 0.1, 0.3));
        const C = L.card;
        box(E.q("card"), lerp(C[0], C[4], k), lerp(C[1], C[5], k), lerp(C[2], C[6], k), lerp(C[3], C[7], k));
        E.q("grid").style.opacity = String(range(v, 0.18, 0.3));

        const k1 = easeOut(range(v, 0.12, 0.26));
        for (const key of ["meta", "title", "role"] as const) {
            const [[x0, y0, s0], [x1, y1, s1]] = L[key];
            place(E.q(key), lerp(x0, x1, k1), lerp(y0, y1, k1), lerp(s0, s1, k1));
        }
        E.q("desc").style.opacity = String(1 - range(v, 0.12, 0.2));
        E.q("summary").style.opacity = String(range(v, 0.2, 0.28));
        E.hl.forEach((h) => (h.style.opacity = String(1 - range(v, 0.14, 0.22))));
        const Ac = L.actions;
        place(E.q("actions"), lerp(Ac[0], Ac[2], k1), lerp(Ac[1], Ac[3], k1));

        // Highlights → numbered badges in a column → pinned onto the diagram.
        const kc = easeOut(range(v, 0.16, 0.28));
        schematic.annotations.forEach((a, i) => {
            const A = E.ann[i];
            if (!A) return;
            const ka = range(v, 0.7 + i * 0.03, 0.8 + i * 0.03);
            const N = L.ann;
            const x0 = lerp(N.x0, N.x1, kc);
            const y0 = lerp(N.y0 + i * N.step0, N.y1 + i * N.step1, kc);
            place(A.badge, lerp(x0, a.bx, easeOut(ka)), lerp(y0, a.by, smooth(ka)));
            A.badge.style.opacity = String(range(v, 0.14, 0.2));
            // On a phone the full caption replaces the short label in the legend.
            const fade = L.phone ? 1 : 0.7;
            A.short.style.opacity = String(range(v, 0.24, 0.3) * (1 - fade * range(v, 0.7 + i * 0.03, 0.74 + i * 0.03)));
            const kt = range(v, 0.76 + i * 0.03, 0.84 + i * 0.03);
            A.text.style.opacity = String(kt);
            const tx = L.phone ? N.shortX : a.tx;
            const ty = L.phone ? N.y1 + i * N.step1 - 2 : a.ty;
            place(A.text, tx, ty + 6 * (1 - easeOut(kt)));
        });

        // Tags: lift → arc flight → pill-to-node shape morph.
        E.tags.forEach((el, i) => {
            const g = geom.current[i];
            if (!g) return;
            const kl = easeOut(range(v, 0.28 + i * 0.012, 0.36 + i * 0.012));
            const kf = range(v, 0.38 + i * 0.015, 0.54 + i * 0.015);
            const ks = easeOut(range(kf, 0.45, 1));
            const f = g.to;
            box(
                el,
                lerp(g.x0, f.x, easeOut(kf)),
                lerp(g.y0 - 10 * kl, f.y, smooth(kf)),
                lerp(g.w0, f.w, ks),
                lerp(28, f.h, ks)
            );
            el.style.borderRadius = `${lerp(14, f.r, ks)}px`;
            el.style.boxShadow =
                kl > 0 && kf < 1 ? `0 ${8 * kl}px ${24 * kl}px rgba(0,0,0,${0.4 * kl})` : "none";
            el.classList.toggle("is-node", g.isNode && kf >= 1);
            const role = E.roles[i];
            if (role) role.style.opacity = String(g.isNode ? range(v, 0.54, 0.6) : 0);
        });

        const kx = easeOut(range(v, 0.54, 0.6));
        E.ext.forEach((el) => {
            el.style.opacity = String(kx);
            el.style.transform = `scale(${lerp(0.9, 1, kx)})`;
        });

        E.edges.forEach((e, i) => {
            const [a, b] = edgeBeat(i, edgeCount);
            const t = range(v, a, b);
            e.path.style.strokeDashoffset = String(e.len * (1 - t));
            if (t > 0 && t < 1) {
                const pt = e.path.getPointAtLength(e.len * t);
                e.tip.setAttribute("cx", String(pt.x));
                e.tip.setAttribute("cy", String(pt.y));
                e.tip.style.opacity = "1";
            } else {
                e.tip.style.opacity = "0";
            }
            if (e.label) e.label.style.opacity = String(range(v, b - 0.01, b + 0.02));
        });
        E.boundary.path.style.strokeDashoffset = String(E.boundary.len * (1 - range(v, 0.62, 0.72)));
    };

    useSceneRender(p, render, [project]);

    // Hold: packets travel the wires. Runs only while the scene is on screen.
    useEffect(() => {
        const r = root.current;
        if (!r) return;
        let raf = 0;
        let visible = false;
        const tick = (now: number) => {
            const E = els.current;
            if (E) {
                // Packets travel each wire as soon as it has been drawn.
                const v = p.get();
                E.edges.forEach((e, i) => {
                    const done = edgeBeat(i, edgeCount)[1];
                    const pk = range(v, done, done + 0.03);
                    if (pk <= 0) {
                        e.pk.style.opacity = "0";
                        return;
                    }
                    const u = (now / 2400 + i * 0.23) % 1;
                    const pt = e.path.getPointAtLength(e.len * u);
                    e.pk.setAttribute("cx", String(pt.x));
                    e.pk.setAttribute("cy", String(pt.y));
                    e.pk.style.opacity = String(pk * (u < 0.12 ? u / 0.12 : u > 0.88 ? (1 - u) / 0.12 : 1));
                });
            }
            if (visible) raf = requestAnimationFrame(tick);
        };
        const io = new IntersectionObserver(([entry]) => {
            visible = entry.isIntersecting;
            cancelAnimationFrame(raf);
            if (visible) raf = requestAnimationFrame(tick);
        });
        io.observe(r);
        return () => {
            io.disconnect();
            cancelAnimationFrame(raf);
        };
    }, [p, edgeCount]);

    return (
        <SceneCanvas tint="124, 146, 255" marker={index === 0 ? { index: 4, label: "Work" } : undefined} {...L.canvas}>
            <div ref={root} className={L.phone ? "wk wk--phone" : "wk"}>
                <div className="wk-head" data-wk="head">
                    <h1 className="scene-title">
                        Featured <span>Projects</span>
                    </h1>
                    <p className="scene-sub">
                        {index === 0
                            ? "End-to-end systems I built, from RESTful API design and EF Core optimization to React/Next.js frontends and Azure deployment."
                            : `Project ${String(index + 1).padStart(2, "0")} of ${String(PROJECTS.length).padStart(2, "0")}`}
                    </p>
                </div>

                <div className="wk-card" data-wk="card">
                    <div className="wk-grid" data-wk="grid" />
                </div>

                <div className="mx-abs wk-meta" data-wk="meta">
                    <span className="wk-pill mono">
                        <Calendar size={12} strokeWidth={2} />
                        {project.period}
                    </span>
                    {project.featured && (
                        <span className="wk-pill wk-pill--featured mono">
                            <Star size={12} strokeWidth={2} fill="currentColor" />
                            Featured
                        </span>
                    )}
                </div>
                <h3 className="mx-abs wk-title" data-wk="title">
                    {project.title}
                </h3>
                <p className="mx-abs wk-role mono" data-wk="role">
                    {project.role}
                </p>
                <p className="mx-abs wk-desc" data-wk="desc" style={{ transform: `translate(${L.desc[0]}px,${L.desc[1]}px)` }}>
                    {project.description}
                </p>
                <p className="mx-abs wk-summary" data-wk="summary" style={{ transform: `translate(${L.summary[0]}px,${L.summary[1]}px)` }}>
                    {project.summary}
                </p>
                {project.highlights.slice(0, 4).map((h, i) => (
                    <p key={h} className="mx-abs wk-hl" title={h} style={{ transform: `translate(${L.hl[0]}px,${L.hl[1] + i * L.hl[2]}px)` }}>
                        {h}
                    </p>
                ))}
                <div className="mx-abs wk-actions" data-wk="actions">
                    {project.repos.map((repo) => (
                        <a key={repo.label} href={repo.url} target="_blank" rel="noopener noreferrer" className="wk-pill wk-action">
                            <SiGithub size={12} />
                            {repo.label}
                            <ArrowUpRight size={12} strokeWidth={2} />
                        </a>
                    ))}
                </div>

                {project.tech.map(({ name, icon: Icon }, ti) => {
                    const target = schematic.tagTargets[name];
                    const node = schematic.nodes.find((n) => n.id === target);
                    return (
                        <div key={name} className="wk-tag" title={name} style={{ "--i": ti } as CSSProperties}>
                            <Icon className="wk-tag-icon" />
                            <span>{name}</span>
                            {node && <span className="wk-tag-role mono">{node.role}</span>}
                        </div>
                    );
                })}
                {schematic.nodes
                    .filter((n) => n.external)
                    .map((n) => (
                        <div
                            key={n.id}
                            className="wk-ext"
                            style={{ left: n.x - NODE_W / 2, top: n.y - NODE_H / 2, width: NODE_W, height: NODE_H }}
                        >
                            {n.label}
                            <small className="mono">{n.role}</small>
                        </div>
                    ))}

                <svg
                    className="wk-svg"
                    viewBox={`0 0 ${L.canvas.width} ${L.canvas.height}`}
                    style={{ width: L.canvas.width, height: L.canvas.height }}
                    aria-hidden="true"
                >
                    <path className="wk-boundary" d={schematic.boundaryD} />
                    {schematic.edges.map((e, i) => (
                        <path key={i} className="wk-edge" d={e.d} />
                    ))}
                    {schematic.edges.map((_, i) => (
                        <circle key={`t${i}`} className="wk-tip" data-wk-tip={i} r={3} />
                    ))}
                    {schematic.edges.map((_, i) => (
                        <circle key={`p${i}`} className="wk-packet" data-wk-pk={i} r={2.2} />
                    ))}
                </svg>
                {schematic.edges.map((e, i) =>
                    e.label ? (
                        <span
                            key={i}
                            className={`wk-edge-label mono${schematic.sideLabels?.[i] ? " is-side" : ""}`}
                            data-wk-label={i}
                            style={{ left: e.lx, top: e.ly }}
                        >
                            {e.label}
                        </span>
                    ) : null
                )}

                {schematic.annotations.map((a, i) => (
                    <span key={`b${i}`} className="mx-abs wk-ann-badge mono" style={{ "--i": i } as CSSProperties}>
                        {i + 1}
                    </span>
                ))}
                {schematic.annotations.map((a, i) => (
                    <span
                        key={`s${i}`}
                        className="mx-abs wk-ann-short mono"
                        data-wk-short={i}
                        style={{ transform: `translate(${L.ann.shortX}px,${L.ann.y1 + 1 + i * L.ann.step1}px)` }}
                    >
                        {a.short}
                    </span>
                ))}
                {schematic.annotations.map((a, i) => (
                    <p key={`t${i}`} className="mx-abs wk-ann-text" data-wk-text={i}>
                        {L.phone && <b className="wk-ann-num mono">{i + 1}</b>}
                        {a.text}
                    </p>
                ))}
            </div>
        </SceneCanvas>
    );
}
